import { useEffect, useState } from "react";
import { useToken } from "@packsize/authentication";

import type { Machine } from "@/schemas/machine";
import { useEnterServiceMode, useLeaveServiceMode, useServiceMachines } from "@/hooks/machines";

import { MachineServiceTemplate } from "./machine-service.template";

export const MachineService = () => {
	const { decodedToken } = useToken();
	const { machines: machineList, isLoading } = useServiceMachines({
		userRoles: decodedToken?.role,
	});
	const enterServiceModeMutation = useEnterServiceMode();
	const leaveServiceModeMutation = useLeaveServiceMode();

	const [selectedMachine, setSelectedMachine] = useState<Machine | undefined>(undefined);
	const [isLeaveServiceModalOpen, setIsLeaveServiceModalOpen] = useState(false);

	useEffect(() => {
		if (selectedMachine && machineList) {
			const machines = machineList.filter((machine) => machine.id === selectedMachine.id);
			if (machines.length > 0) {
				setSelectedMachine(machines[0]);
			}
		}
	}, [machineList, selectedMachine]);

	const handleEnterServiceMode = async (machine: Machine): Promise<void> => {
		try {
			await enterServiceModeMutation.mutateAsync(machine);
			setSelectedMachine(machine);
		} catch (error) {
			console.error("Failed to enter service mode:", error);
		}
	};

	const handleOpenLeaveServiceModal = (machine: Machine) => {
		setSelectedMachine(machine);
		setIsLeaveServiceModalOpen(true);
	};

	const handleCloseLeaveServiceModal = () => {
		setIsLeaveServiceModalOpen(false);
	};

	const handleConfirmLeaveService = async (): Promise<void> => {
		if (selectedMachine) {
			try {
				await leaveServiceModeMutation.mutateAsync(selectedMachine);
				setSelectedMachine(undefined);
				handleCloseLeaveServiceModal();
			} catch (error) {
				console.error("Failed to leave service mode:", error);
				handleCloseLeaveServiceModal();
			}
		}
	};

	return (
		<MachineServiceTemplate
			machineList={machineList}
			isLoading={isLoading}
			selectedMachine={selectedMachine}
			isLeaveServiceModalOpen={isLeaveServiceModalOpen}
			isLeaveServiceMutationPending={leaveServiceModeMutation.isPending}
			onEnterServiceMode={handleEnterServiceMode}
			onSelectMachine={setSelectedMachine}
			onOpenLeaveServiceModal={handleOpenLeaveServiceModal}
			onCloseLeaveServiceModal={handleCloseLeaveServiceModal}
			onConfirmLeaveService={handleConfirmLeaveService}
		/>
	);
};
